import {
  MenuItem,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import AdminDashboardLayout from "../../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { useAnalytics, useAuth, useFirestore } from "reactfire";
import { useFeedback } from "../../../../providers/feedback/feedback";
import { logEvent } from "firebase/analytics";
import { addDoc, collection } from "firebase/firestore";
import { getApp, getApps, initializeApp } from "firebase/app";
import { firebaseConfig } from "../../../../index";
import { AdminRoleId, ROLE_OPTIONS } from "../../../../utils/admin_access";
import { getFirebaseAuthErrorMessage } from "../../../../utils/auth_error_messages";
import { toTitleCaseName } from "../../../../utils/title_case";

interface Props {}

function CreateAdmin() {
  const [adminName, setAdminName] = useState("");
  const [adminEmailId, setAdminEmailId] = useState("");
  const [password, setPassword] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<AdminRoleId>("admin");
  const [errorMsg, setErrorMsg] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const auth = useAuth();
  const analytics = useAnalytics();

  const submitForm = async () => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
    if (!emailRegex?.test(adminEmailId)) {
      setErrorMsg({ ...errorMsg, email: "Please enter valid email" });
    } else if (password.length < 6) {
      setErrorMsg({
        ...errorMsg,
        password: "Password should be at least 6 characters",
      });
    } else {
      const roleConfig = ROLE_OPTIONS.find((role) => role.role_id === selectedRole);
      const appName = "secondary-admin-user-creation";
      const secondaryApp =
        getApps().find((app) => app.name === appName) ||
        initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuth(secondaryApp);

      try {
        setIsLoading(true);
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          adminEmailId,
          password,
        );

        logEvent(analytics, "create_user", { uid: userCredential?.user.uid });
        const usersCollection = collection(firestore, "admins");
        await addDoc(usersCollection, {
          createdAt: Date().toString(),
          updatedAt: Date().toString(),
          email_id: adminEmailId,
          name: toTitleCaseName(adminName),
          role: roleConfig?.role || "Admin",
          role_id: roleConfig?.role_id || "admin",
          modules: roleConfig?.modules || ["all"],
          uid: userCredential?.user?.uid,
          createdBy: auth.currentUser?.uid || "",
        });
        await secondaryAuth.signOut();
        showSnackbar("User Added Successfully");
        navigate("/admin/users");
      } catch (error: any) {
        console.error("Failed to create admin user", error);
        const message = getFirebaseAuthErrorMessage(
          error,
          "Failed to create admin user. Please try again.",
        );
        showSnackbar(message);
        if (error?.code === "auth/weak-password") {
          setErrorMsg({
            ...errorMsg,
            password: "Password should be at least 6 characters",
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }
  return (
    <AdminDashboardLayout>
      <Grid container>
        <Grid item md={6} lg={6} xl={6}>
          <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
            <CardContent>
              <Box display="flex" flexDirection="column" pl={1}>
                <Box flexDirection="row" display="flex" alignItems="center">
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack></ArrowBack>
                  </IconButton>
                  <Typography variant="h6" fontWeight="600">
                    Add New User
                  </Typography>
                </Box>
                <Box sx={{ mt: 4 }}>
                  <Box>
                    <TextField
                      fullWidth
                      label="User Name"
                      placeholder="Enter user name"
                      value={adminName}
                      onChange={(event) => {
                        setAdminName(toTitleCaseName(event?.target.value));
                      }}
                      required
                    />
                  </Box>
                  <Box mt={3}>
                    <TextField
                      fullWidth
                      label="Email ID"
                      placeholder="Enter Email id"
                      value={adminEmailId}
                      onChange={(event) => {
                        setAdminEmailId(event?.target.value);
                      }}
                      FormHelperTextProps={{ sx: { color: "red" } }}
                      helperText={errorMsg?.email}
                      required
                    />

                    <Box mt={2}>
                      <TextField
                        select
                        fullWidth
                        label="Access Role"
                        value={selectedRole}
                        onChange={(event) => {
                          setSelectedRole(event.target.value as AdminRoleId);
                        }}
                      >
                        {ROLE_OPTIONS.map((role) => (
                          <MenuItem key={role.role_id} value={role.role_id}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    <Box mt={2}>
                      <TextField
                        placeholder="Enter  password"
                        fullWidth
                        value={password}
                        sx={{ mt: 1 }}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setErrorMsg({ ...errorMsg, password: "" });
                        }}
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        helperText={errorMsg?.password}
                        FormHelperTextProps={{ sx: { color: "red" } }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    mt={4}
                  >
                    <Button
                      onClick={() => navigate("/admin/users")}
                      variant="outlined"
                      sx={{
                        minWidth: 200,
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      onClick={submitForm}
                      sx={{
                        minWidth: 200,
                      }}
                      variant="contained"
                    >
                      {isLoading ? "Creating..." : "Add User"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AdminDashboardLayout>
  );
}

export default CreateAdmin;
